import { ButtonHTMLAttributes, useState } from "react";
import { Note, NoteReply, SaveNoteReplayModel } from "../../../types/note/Note";
import { EditNoteReply } from "./EditNoteReply";
import dateTimeUtils from "../../../utils/dateTime.utils";
import { isRequesting } from "../../../utils/request-state.utils";
import { stringUtils } from "../../../utils/string.utils";
import { user } from "../../../user/user";
import { Confirm } from "../../alerts/Confirm";
import { RequestState } from "../../../constants/request-state";
import IconSVG from "../../../styles/svg-icons";
import { OnHoverTooltip } from "../../common/OnHoverTooltip";

interface Props {
    reply: NoteReply
    note: Note
    saveRequestState: RequestState
    deleteRequestState: RequestState
    onSave: (reply: SaveNoteReplayModel) => Promise<boolean>
    onDelete: (reply: NoteReply) => void
}
export function NoteReplyView({ reply, note, saveRequestState, deleteRequestState, onSave, onDelete }: Props) {
    const [isEdit, setEdit] = useState(false);
    const isMy = stringUtils.equalsIgnoringCase(reply.user.email, user.current()?.email ?? '');

    const handleSave = async (text: string) => {
        const isSaved = await onSave({ id: reply.id, noteId: reply.noteId, text });
        if (isSaved) {
            setEdit(false);
        }
    }

    return (
        <div className="notes-item notes-reply-item">
            <div className="notes-head">
                <span className="notes-user-name">
                    <span className="notes-user-info">
                        <OnHoverTooltip overlay={`${reply.user.firstName} ${reply.user.lastName}`}>
                            {reply.user.firstName} {reply.user.lastName}
                        </OnHoverTooltip>
                    </span>
                </span>
                <span className="notes-publish-date">
                    {dateTimeUtils.utcToLocalString(reply.submittedDate)}
                </span>
                {isMy &&
                    <div className="notes-action-buttons">
                        <EditReplyButton
                            disabled={isEdit || isRequesting(saveRequestState, deleteRequestState)}
                            onClick={() => setEdit(true)}
                        />
                        <DeleteReplyButton
                            disabled={isRequesting(saveRequestState, deleteRequestState)}
                            onClick={() => onDelete(reply)}
                        />
                    </div>
                }
            </div>
            <div>{isEdit ?
                <EditNoteReply
                    text={reply.text}
                    note={note}
                    requestState={saveRequestState}
                    onSave={handleSave}
                    onCancel={() => setEdit(false)}
                />
                : reply.text}</div>
        </div>
    )
}

function EditReplyButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <button type="button" aria-label="edit reply" className="btn-link" {...props}>
            <IconSVG
                name="edit-pencil"
                width={16}
                height={16}
            />
        </button>
    );
}

function DeleteReplyButton({ onClick, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
    const [isConfirm, setConfirm] = useState(false)

    return (
        <>
            <button type="button" aria-label="delete reply" className="btn-link btn-danger" {...props} onClick={() => setConfirm(true)}>
                <IconSVG
                    name="basket"
                    width={16}
                    height={16}
                />
            </button>
            {
                isConfirm &&
                <Confirm
                    renderInBody
                    text="Are you sure you want to delete the reply message?"
                    onConfirm={(e: React.MouseEvent<HTMLButtonElement>) => {
                        onClick?.(e);
                        setConfirm(false);
                    }}
                    onCancel={() => setConfirm(false)}
                />
            }
        </>
    );
}